/**
 * Album generation narration steps.
 *
 * Album rendering takes several minutes, so instead of a bare spinner or
 * generic tips the progress card narrates what the pipeline is actually doing.
 * The sequence is built dynamically from the sections the user chose to include
 * (materials, blueprints, safety, etc.) so the narration matches the document
 * being produced. The active step is derived from the card's time-based
 * progress, so no fake per-step completion is claimed.
 */

export interface AlbumSectionFlags {
  includeMaterials: boolean;
  /** Estimate is included by default (opt-out only) and drives the cost steps. */
  includeEstimate: boolean;
  includeBlueprints: boolean;
  includePreparationGuide: boolean;
  includeSafetyRequirements: boolean;
  includeBuildingRegulations: boolean;
  hasSpecialRequirements: boolean;
}

interface AlbumStepDef {
  /** Sub-key under the `album.generatingSteps` i18n namespace. */
  key: string;
  /** Included only when this returns true; always-on steps omit it. */
  when?: (flags: AlbumSectionFlags) => boolean;
}

// Canonical order: analyze the design, gather each opted-in content section,
// then assemble, finalize and render the document. Conditional steps sit in the
// middle so the always-on bookends (analysis, PDF render) frame the flow.
const ALBUM_STEP_SEQUENCE: readonly AlbumStepDef[] = [
  { key: 'analyzing' },
  { key: 'curatingImages' },
  { key: 'annotating' },
  { key: 'materials', when: (f) => f.includeMaterials },
  { key: 'components', when: (f) => f.includeMaterials },
  { key: 'areas', when: (f) => f.includeEstimate },
  { key: 'market', when: (f) => f.includeEstimate },
  { key: 'pricing', when: (f) => f.includeEstimate },
  { key: 'blueprints', when: (f) => f.includeBlueprints },
  { key: 'dimensions', when: (f) => f.includeBlueprints },
  { key: 'prepPlan', when: (f) => f.includePreparationGuide },
  { key: 'safety', when: (f) => f.includeSafetyRequirements },
  { key: 'localReqs', when: (f) => f.includeBuildingRegulations },
  { key: 'buildingCodes', when: (f) => f.includeBuildingRegulations },
  { key: 'specialReqs', when: (f) => f.hasSpecialRequirements },
  { key: 'layout' },
  { key: 'cover' },
  { key: 'pages' },
  { key: 'typesetting' },
  { key: 'consistency' },
  { key: 'pdf' },
];

/** Ordered `generatingSteps.*` sub-keys for the chosen sections. */
export function buildAlbumStepKeys(flags: AlbumSectionFlags): string[] {
  return ALBUM_STEP_SEQUENCE.filter((s) => !s.when || s.when(flags)).map(
    (s) => s.key
  );
}

/**
 * Map a 0–100 progress value to the active step index. The last step is held
 * as progress approaches 100 (the card stalls near completion), so we never
 * advance past the final "rendering" step until the real album lands.
 */
export function activeAlbumStepIndex(progress: number, stepCount: number): number {
  if (stepCount <= 0) return 0;
  const index = Math.floor((progress / 100) * stepCount);
  return Math.min(Math.max(index, 0), stepCount - 1);
}
